/**
 * Policies — Company Policies module (Iter 101).
 * Route: /app/policies  (list)  ·  /app/policies/:pid (detail)
 */
import { useEffect, useState } from "react";
import { Link, useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useWorkspacePerms } from "@/lib/useWorkspacePerms";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import RichTextEditor from "@/components/RichTextEditor";
import SmartRichText, { SmartRichTextDisplay } from "@/components/SmartRichText";
import { toast, Toaster } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { ScrollText, Plus, ArrowLeft, CheckCircle2, Archive, FileText, Users, Paperclip, Upload, Trash2, Download, ShieldCheck, ChevronDown, ChevronRight, AlertTriangle } from "lucide-react";
import ConfirmDialog from "@/components/ConfirmDialog";

const CATS = ["hr", "security", "code_of_conduct", "finance", "general", "it", "compliance"];
const CAT_LABEL = {
  hr: "HR", security: "Security", code_of_conduct: "Code of Conduct",
  finance: "Finance", general: "General", it: "IT", compliance: "Compliance",
};

export default function PoliciesPage() {
  const { activeWorkspaceId: wsId } = useAuth();
  const { can } = useWorkspacePerms();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("all");
  const [sp] = useSearchParams();
  const [editing, setEditing] = useState(null);
  // R148g · Feb 2026 — Tabs for Policies / Compliance (admin/HR only)
  const [tab, setTab] = useState("policies");
  const canManage = can("policy.manage");
  const canReport = can("policy.acknowledge.report");

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/policies`);
      setRows(r.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [wsId]);

  const pendingFilter = sp.get("filter") === "needs-ack";
  const filtered = rows.filter((r) => {
    if (cat !== "all" && r.category !== cat) return false;
    if (pendingFilter && (!r.requires_acknowledgement
        || (r.my_ack && r.my_ack.policy_version === r.version))) return false;
    return true;
  });

  return (
    <div className="space-y-4" data-testid="policies-page">
      <Toaster richColors position="top-center" />
      <PageHeader title="Company policies"
                    subtitle="One source of truth for company rules, HR, security & code of conduct."
                    icon={ScrollText}
                    actions={canManage && tab === "policies" ? (
                      <Button size="sm" className="rounded-none" onClick={() => setEditing({})}
                              data-testid="policies-new-btn">
                        <Plus className="w-3.5 h-3.5 mr-1" /> New policy
                      </Button>
                    ) : null} />

      {/* Tab switcher — Compliance only visible to reporters/admins */}
      {canReport && (
        <div className="flex items-center gap-1 border-b border-border" data-testid="policies-tabs">
          <button
            onClick={() => setTab("policies")}
            className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === "policies" ? "border-primary text-primary font-semibold" : "border-transparent hover:text-foreground text-muted-foreground"}`}
            data-testid="policies-tab-policies"
          >
            <FileText className="w-3.5 h-3.5 inline mr-1" /> All policies
          </button>
          <button
            onClick={() => setTab("compliance")}
            className={`px-3 py-2 text-xs uppercase tracking-widest border-b-2 -mb-px ${tab === "compliance" ? "border-primary text-primary font-semibold" : "border-transparent hover:text-foreground text-muted-foreground"}`}
            data-testid="policies-tab-compliance"
          >
            <ShieldCheck className="w-3.5 h-3.5 inline mr-1" /> Compliance dashboard
          </button>
        </div>
      )}

      {tab === "compliance" && canReport ? (
        <ComplianceDashboard wsId={wsId} canManage={canManage} />
      ) : (
        <>
          {/* Category filter */}
          <div className="flex flex-wrap gap-1 items-center" data-testid="policies-filters">
            <button onClick={() => setCat("all")} data-testid="policies-cat-all"
                    className={`text-xs px-2 py-1 border ${cat === "all" ? "bg-primary text-primary-foreground" : "border-border bg-background"}`}>
              All ({rows.length})
            </button>
            {CATS.map((c) => (
              <button key={c} onClick={() => setCat(c)}
                       data-testid={`policies-cat-${c}`}
                       className={`text-xs px-2 py-1 border ${cat === c ? "bg-primary text-primary-foreground" : "border-border bg-background"}`}>
                {CAT_LABEL[c]}
              </button>
            ))}
            {pendingFilter && (
              <span className="ml-2 text-[10px] uppercase tracking-widest bg-amber-100 text-amber-700 px-2 py-1">
                Showing: needs my acknowledgement
              </span>
            )}
          </div>

          {loading ? (
            <div className="text-sm text-muted-foreground">Loading…</div>
          ) : filtered.length === 0 ? (
            <div className="border border-dashed border-border p-10 text-center" data-testid="policies-empty">
              <FileText className="w-8 h-8 mx-auto text-muted-foreground" />
              <p className="mt-3 font-semibold">No policies here yet</p>
              <p className="text-sm text-muted-foreground mt-1">
                {canManage ? "Click 'New policy' to add one." : "Ask your workspace admin to publish policies."}
              </p>
            </div>
          ) : (
            <div className="border border-border divide-y divide-border" data-testid="policies-list">
              {filtered.map((p) => (
                <PolicyRow key={p.id} p={p} canManage={canManage} />
              ))}
            </div>
          )}
        </>
      )}

      {editing && (
        <PolicyEditor wsId={wsId} policy={editing.id ? editing : null}
                        onDone={() => { setEditing(null); load(); }}
                        onCancel={() => setEditing(null)} />
      )}
    </div>
  );
}

// ─────────────── R148g · Compliance Dashboard ───────────────

function ComplianceDashboard({ wsId, canManage }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState({ block_users_until_acknowledged: false });
  const [savingGate, setSavingGate] = useState(false);
  const [expandedId, setExpandedId] = useState(null);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const [c, s] = await Promise.all([
        api.get(`/workspaces/${wsId}/policies/compliance`),
        api.get(`/workspaces/${wsId}/policies/settings`),
      ]);
      setData(c.data);
      setSettings(s.data || { block_users_until_acknowledged: false });
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load compliance")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const toggleGate = async (next) => {
    setSavingGate(true);
    try {
      const { data } = await api.put(`/workspaces/${wsId}/policies/settings`, {
        block_users_until_acknowledged: next,
      });
      setSettings(data);
      toast.success(next
        ? "Gate enabled — users with pending acknowledgements will be blocked"
        : "Gate disabled");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSavingGate(false); }
  };

  const remind = async () => {
    try {
      const { data } = await api.post(`/workspaces/${wsId}/policies/ack-reminders/send-now`);
      toast.success(`Reminder sweep complete · ${data?.sent || 0} sent`);
    } catch (e) { toast.error(apiErrorMessage(e, "Reminder failed")); }
  };

  if (loading || !data) return <div className="text-sm text-muted-foreground p-6">Loading compliance…</div>;

  const rows = data.policies || [];
  const totals = data.totals || {};

  return (
    <div className="space-y-4" data-testid="policies-compliance-dashboard">
      {/* Admin gate setting */}
      {canManage && (
        <div className="border-2 border-primary/40 bg-primary/5 p-4 flex items-start gap-3" data-testid="policy-gate-setting">
          <AlertTriangle className={`w-5 h-5 mt-0.5 shrink-0 ${settings.block_users_until_acknowledged ? "text-amber-600" : "text-muted-foreground"}`} />
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm">
              Block users from using the system until every required policy is acknowledged
            </div>
            <div className="text-xs text-muted-foreground mt-0.5">
              When enabled, any team member with pending acknowledgements sees a
              full-screen popup on every page until they acknowledge each
              required policy. They can still read the full policy text before
              acknowledging.
            </div>
          </div>
          <label className="inline-flex items-center gap-2 shrink-0 cursor-pointer">
            <input
              type="checkbox"
              checked={!!settings.block_users_until_acknowledged}
              onChange={(e) => toggleGate(e.target.checked)}
              disabled={savingGate}
              className="w-4 h-4"
              data-testid="policy-gate-toggle"
            />
            <span className="text-xs font-semibold">
              {settings.block_users_until_acknowledged ? "Enabled" : "Disabled"}
            </span>
          </label>
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Kpi label="Policies tracked" value={totals.policies_tracked || 0} />
        <Kpi label="Team members" value={totals.members || 0} />
        <Kpi label="Overall coverage" value={`${totals.overall_coverage_pct ?? 0}%`}
              tone={(totals.overall_coverage_pct ?? 0) >= 95 ? "green" : (totals.overall_coverage_pct ?? 0) >= 70 ? "amber" : "red"} />
        <Kpi label="Pending acks" value={totals.pending_acks_total || 0}
              tone={(totals.pending_acks_total || 0) > 0 ? "amber" : "green"} />
      </div>

      {/* Reminder button */}
      {canManage && (
        <div className="flex items-center justify-end">
          <Button variant="outline" size="sm" className="rounded-none" onClick={remind} data-testid="policy-compliance-remind">
            <Users className="w-3.5 h-3.5 mr-1" /> Send reminder sweep now
          </Button>
        </div>
      )}

      {/* Per-policy compliance table */}
      {rows.length === 0 ? (
        <div className="border border-dashed border-border p-8 text-center text-xs text-muted-foreground">
          No published policies require acknowledgement yet. Publish a policy and check
          &quot;Require every workspace member to acknowledge&quot; to see compliance here.
        </div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="policy-compliance-list">
          {rows.map((r) => (
            <div key={r.policy_id} data-testid={`policy-compliance-row-${r.policy_id}`}>
              <button
                type="button"
                onClick={() => setExpandedId((v) => v === r.policy_id ? null : r.policy_id)}
                className="w-full flex items-center gap-3 p-3 hover:bg-muted/40 text-left"
                data-testid={`policy-compliance-expand-${r.policy_id}`}
              >
                {expandedId === r.policy_id ? <ChevronDown className="w-4 h-4 text-muted-foreground shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />}
                <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 w-24 shrink-0">
                  {CAT_LABEL[r.category] || r.category}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm truncate">{r.title}</div>
                  <div className="text-[10px] text-muted-foreground">v{r.version} · {r.acknowledged_count} of {r.total_members} acknowledged</div>
                </div>
                {/* Progress bar */}
                <div className="w-40 hidden md:block shrink-0">
                  <div className="h-2 bg-muted border border-border">
                    <div
                      className={`h-full ${r.coverage_pct >= 95 ? "bg-emerald-500" : r.coverage_pct >= 70 ? "bg-amber-500" : "bg-rose-500"}`}
                      style={{ width: `${r.coverage_pct}%` }}
                    />
                  </div>
                </div>
                <span className={`text-sm font-bold tabular-nums w-14 text-right shrink-0 ${r.coverage_pct >= 95 ? "text-emerald-700" : r.coverage_pct >= 70 ? "text-amber-700" : "text-rose-700"}`}>
                  {r.coverage_pct}%
                </span>
                {r.pending_count > 0 && (
                  <span className="text-[10px] uppercase font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 shrink-0">
                    {r.pending_count} pending
                  </span>
                )}
                <Link
                  to={`/app/policies/${r.policy_id}`}
                  onClick={(e) => e.stopPropagation()}
                  className="text-[10px] text-primary hover:underline shrink-0"
                  data-testid={`policy-compliance-view-${r.policy_id}`}
                >
                  View
                </Link>
              </button>
              {expandedId === r.policy_id && (
                <div className="border-t border-border bg-muted/20 p-3 space-y-2" data-testid={`policy-compliance-pending-${r.policy_id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                    Pending acknowledgements ({r.pending_users?.length || 0})
                  </div>
                  {(!r.pending_users || r.pending_users.length === 0) ? (
                    <div className="text-xs italic text-emerald-700 flex items-center gap-1">
                      <CheckCircle2 className="w-3.5 h-3.5" /> Everyone has acknowledged this policy.
                    </div>
                  ) : (
                    <ul className="divide-y divide-border border border-border max-h-80 overflow-y-auto">
                      {r.pending_users.map((u) => (
                        <li key={u.user_id} className="p-2 flex items-center gap-2 text-xs" data-testid={`policy-compliance-user-${r.policy_id}-${u.user_id}`}>
                          <span className="font-semibold w-48 truncate">{u.name}</span>
                          <span className="text-muted-foreground truncate flex-1">{u.email}</span>
                          <a
                            href={`mailto:${u.email}?subject=${encodeURIComponent(`Please acknowledge: ${r.title}`)}`}
                            className="text-[10px] text-primary hover:underline shrink-0"
                            data-testid={`policy-compliance-mail-${r.policy_id}-${u.user_id}`}
                          >
                            Email
                          </a>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Kpi({ label, value, tone }) {
  const toneCls = tone === "green" ? "text-emerald-700"
                : tone === "amber" ? "text-amber-700"
                : tone === "red"   ? "text-rose-700"
                : "text-foreground";
  return (
    <div className="border border-border p-3 bg-background">
      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</div>
      <div className={`text-lg font-bold tabular-nums ${toneCls}`}>{value}</div>
    </div>
  );
}

function PolicyRow({ p, canManage }) {
  const needsAck = p.requires_acknowledgement && (!p.my_ack || p.my_ack.policy_version !== p.version);
  return (
    <Link to={`/app/policies/${p.id}`}
           className="block p-3 hover:bg-muted/50 transition-colors"
           data-testid={`policy-row-${p.id}`}>
      <div className="flex items-center gap-3">
        <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 w-24">
          {CAT_LABEL[p.category] || p.category}
        </span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm truncate">{p.title}</div>
          <div className="text-[10px] text-muted-foreground mt-0.5">
            v{p.version} · updated {new Date(p.updated_at).toLocaleDateString()}
            {p.status !== "published" && <span className="ml-2 uppercase font-bold text-amber-600">{p.status}</span>}
          </div>
        </div>
        {needsAck && (
          <span className="text-[10px] uppercase font-bold px-2 py-0.5 bg-amber-100 text-amber-700 rounded"
                data-testid={`policy-needs-ack-${p.id}`}>
            Ack required
          </span>
        )}
        {p.my_ack && !needsAck && (
          <CheckCircle2 className="w-4 h-4 text-emerald-500" />
        )}
      </div>
    </Link>
  );
}

export function PolicyDetail() {
  const { pid } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { can } = useWorkspacePerms();
  const [p, setP] = useState(null);
  const [ackReport, setAckReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(false);
  const canManage = can("policy.manage");
  const canReport = can("policy.acknowledge.report");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/policies/${pid}`);
      setP(r.data);
      if (canReport && r.data.requires_acknowledgement) {
        try {
          const rr = await api.get(`/policies/${pid}/acknowledgements`);
          setAckReport(rr.data);
        } catch { /* silent */ }
      }
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [pid]); // eslint-disable-line

  const ack = async () => {
    setBusy(true);
    try {
      await api.post(`/policies/${pid}/acknowledge`, { signature: user?.name });
      toast.success("Acknowledged");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Ack failed")); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    try { await api.post(`/policies/${pid}/publish`); toast.success("Published"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Publish failed")); }
  };
  const archive = async () => {
    if (!window.confirm("Archive this policy?")) return;
    try { await api.post(`/policies/${pid}/archive`); toast.success("Archived"); nav("/app/policies"); }
    catch (e) { toast.error(apiErrorMessage(e, "Archive failed")); }
  };

  if (loading || !p) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const needsAck = p.requires_acknowledgement && (!p.my_ack || p.my_ack.policy_version !== p.version);

  return (
    <div className="space-y-4" data-testid="policy-detail-page">
      <Toaster richColors position="top-center" />
      <Link to="/app/policies" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
        <ArrowLeft className="w-3 h-3" /> All policies
      </Link>
      <div className="border border-border p-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5">
            {CAT_LABEL[p.category] || p.category}
          </span>
          <span className="text-[10px] text-muted-foreground">v{p.version} · {p.status}</span>
          {p.my_ack && !needsAck && (
            <span className="text-[10px] uppercase font-bold bg-emerald-100 text-emerald-700 px-2 py-0.5 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" /> Acknowledged
            </span>
          )}
        </div>
        <h1 className="text-2xl font-bold" data-testid="policy-title">{p.title}</h1>
        {p.effective_from && (
          <div className="text-xs text-muted-foreground mt-1">Effective from {p.effective_from}</div>
        )}
        <SmartRichTextDisplay
          html={p.body_html}
          className="mt-6"
          testid="policy-body"
        />

        {needsAck && (
          <div className="mt-6 border border-amber-300 bg-amber-50 p-3 rounded"
               data-testid="policy-ack-cta">
            <div className="text-sm font-semibold mb-1">Your acknowledgement is required.</div>
            <div className="text-xs text-muted-foreground mb-2">
              Clicking below records your name, email and timestamp as an official acknowledgement of this policy (v{p.version}).
            </div>
            <Button size="sm" className="rounded-none" onClick={ack} disabled={busy}
                     data-testid="policy-ack-btn">
              {busy ? "Recording…" : "I have read and understood this policy"}
            </Button>
          </div>
        )}

        {canManage && (
          <div className="mt-6 pt-4 border-t border-border flex gap-2 flex-wrap">
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditing(true)}
                     data-testid="policy-edit-btn">
              Edit
            </Button>
            {p.status !== "published" && (
              <Button size="sm" className="rounded-none" onClick={publish} data-testid="policy-publish-btn">Publish</Button>
            )}
            {p.status !== "archived" && (
              <Button size="sm" variant="outline" className="rounded-none" onClick={archive}
                       data-testid="policy-archive-btn">
                <Archive className="w-3.5 h-3.5 mr-1" /> Archive
              </Button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <PolicyEditor wsId={p.workspace_id} policy={p}
                        onDone={() => { setEditing(false); load(); }}
                        onCancel={() => setEditing(false)} />
      )}

      {ackReport && (
        <div className="border border-border p-3" data-testid="policy-ack-report">
          <div className="text-[11px] uppercase tracking-widest font-semibold mb-2 flex items-center gap-2">
            <Users className="w-3 h-3" /> Acknowledgements &mdash; {ackReport.acknowledged_count} of {ackReport.total_members} ({ackReport.coverage_pct}%)
          </div>
          <div className="max-h-64 overflow-y-auto divide-y divide-border text-xs">
            {(ackReport.rows || []).slice(0, 200).map((r) => (
              <div key={r.id} className="py-1.5 flex items-center gap-2">
                <span className="font-semibold w-40 truncate">{r.user_name}</span>
                <span className="text-muted-foreground truncate flex-1">{r.user_email}</span>
                <span className="text-[10px] text-muted-foreground">{new Date(r.acknowledged_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PolicyEditor({ wsId, policy, onDone, onCancel }) {
  const isNew = !policy;
  const { activeWorkspaceId } = useAuth();
  const [form, setForm] = useState(() => ({
    title: policy?.title || "",
    body_html: policy?.body_html || "",
    category: policy?.category || "general",
    requires_acknowledgement: policy?.requires_acknowledgement || false,
    effective_from: policy?.effective_from || "",
  }));
  const [saving, setSaving] = useState(false);
  const save = async () => {
    if (!form.title.trim() || !form.body_html.trim()) { toast.error("Title + body required"); return; }
    setSaving(true);
    try {
      if (isNew) await api.post(`/workspaces/${wsId}/policies`, form);
      else await api.patch(`/policies/${policy.id}`, form);
      toast.success(isNew ? "Draft created" : "Saved");
      onDone();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" data-testid="policy-editor-modal">
      <div className="bg-background border border-border w-full max-w-3xl max-h-[calc(100vh-2rem)] flex flex-col">
        <div className="px-4 py-3 border-b border-border font-semibold shrink-0">
          {isNew ? "New policy" : `Edit: ${policy.title}`}
        </div>
        <div className="p-4 space-y-3 overflow-y-auto flex-1 min-h-0">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Input placeholder="Policy title" value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    data-testid="policy-editor-title" />
            <select className="h-9 border border-border px-2 text-sm bg-background"
                    value={form.category}
                    onChange={(e) => setForm({ ...form, category: e.target.value })}
                    data-testid="policy-editor-category">
              {CATS.map((c) => <option key={c} value={c}>{CAT_LABEL[c]}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-muted-foreground">Body</label>
            <SmartRichText value={form.body_html}
                             onChange={(html) => setForm({ ...form, body_html: html })}
                             placeholder="Write your policy. Type `/` for headings, lists, quotes, images, or `/ai` to have Claude polish your draft."
                             mediaMode="upload"
                             workspaceId={activeWorkspaceId || wsId}
                             minRows={8}
                             maxHeight={500}
                             help="Structure your policy with headings (Purpose, Scope, Policy, Enforcement, References). Use `/ai improve` to polish tone."
                             testid="policy-editor-body" />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.requires_acknowledgement}
                    onChange={(e) => setForm({ ...form, requires_acknowledgement: e.target.checked })}
                    data-testid="policy-editor-requires-ack" />
            <span>Require every workspace member to acknowledge this policy</span>
          </label>
          {policy?.id && <PolicyAttachments policyId={policy.id} />}
        </div>
        <div className="flex justify-end gap-2 px-4 py-3 border-t border-border bg-muted/20 shrink-0">
          <Button size="sm" variant="outline" className="rounded-none" onClick={onCancel}>Cancel</Button>
          <Button size="sm" className="rounded-none" onClick={save} disabled={saving}
                   data-testid="policy-editor-save">
            {saving ? "Saving…" : (isNew ? "Create draft" : "Save")}
          </Button>
        </div>
      </div>
    </div>
  );
}


// ─────────────── Policy attachments (PDFs/docs via Emergent Object Storage) ───────────────

function PolicyAttachments({ policyId }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [confirmTarget, setConfirmTarget] = useState(null); // {id, filename}

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/attachments/policy/${policyId}`);
      setRows(data || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [policyId]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("File too large (max 25 MB)"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/attachments/policy/${policyId}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`${file.name} uploaded`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Upload failed")); }
    finally { setBusy(false); }
  };

  const doRemove = async () => {
    if (!confirmTarget) return;
    try {
      await api.delete(`/attachments/${confirmTarget.id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const download = async (aid, filename) => {
    // Auth requires JWT bearer — fetch → blob → object URL.
    try {
      const resp = await api.get(`/attachments/file/${aid}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url; a.download = filename || "attachment";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErrorMessage(e, "Download failed")); }
  };

  return (
    <div className="border border-border p-3 space-y-2" data-testid="policy-attachments">
      <div className="flex items-center gap-2">
        <Paperclip className="w-4 h-4 text-muted-foreground" />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex-1">
          Attachments (PDFs, DOCX, images — max 25 MB each)
        </div>
        <label className="inline-flex items-center gap-1 border border-border px-2 py-1 text-xs cursor-pointer hover:bg-secondary/30" data-testid="policy-attach-upload-btn">
          <Upload className="w-3 h-3" />
          {busy ? "Uploading…" : "Upload"}
          <input
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,.txt,.png,.jpg,.jpeg,.webp"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; }}
            data-testid="policy-attach-file-input"
          />
        </label>
      </div>
      {loading ? <div className="text-[11px] text-muted-foreground">Loading…</div>
        : rows.length === 0
          ? <div className="text-[11px] italic text-muted-foreground">No attachments yet.</div>
          : (
            <ul className="divide-y divide-border border border-border" data-testid="policy-attach-list">
              {rows.map((a) => (
                <li key={a.id} className="p-2 flex items-center gap-2 text-xs hover:bg-secondary/20" data-testid={`policy-attach-row-${a.id}`}>
                  <FileText className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="truncate">{a.filename}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {Math.round((a.size || 0) / 1024)} KB · uploaded by {a.uploader_name || "someone"} · {(a.created_at || "").slice(0, 10)}
                    </div>
                  </div>
                  <button type="button" onClick={() => download(a.id, a.filename)} title="Download"
                    className="px-1 py-0.5 hover:text-primary" data-testid={`policy-attach-dl-${a.id}`}>
                    <Download className="w-3.5 h-3.5" />
                  </button>
                  <button type="button" onClick={() => setConfirmTarget({ id: a.id, filename: a.filename })} title="Remove"
                    className="px-1 py-0.5 hover:text-destructive" data-testid={`policy-attach-del-${a.id}`}>
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ul>
          )}
      <ConfirmDialog
        open={!!confirmTarget}
        onOpenChange={(o) => { if (!o) setConfirmTarget(null); }}
        title="Remove attachment?"
        description={confirmTarget ? `"${confirmTarget.filename}" will be permanently removed from this policy.` : ""}
        confirmLabel="Remove"
        destructive
        onConfirm={doRemove}
        testid="confirm-remove-policy-attachment"
      />
    </div>
  );
}
